/**
 * ElecBid — Hiring pipeline
 *
 * Kanban board of job candidates. Candidates arrive automatically when you
 * forward a resume email to ap@browning-electric.ca with "/hire A" (or B/C)
 * in the subject or body — the gmail poller files it here within ~a minute.
 * Cards drag between stages on desktop; on mobile use the stage buttons in
 * the detail panel. Notes, category, and contact info are editable in-app.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useSession } from "../App";
import { apiFetch } from "../lib/apiFetch";

const STAGES = [
  { key: "new",         label: "New",         color: "#60a5fa" },
  { key: "considering", label: "Considering", color: "#fbbf24" },
  { key: "interview",   label: "Interviewed", color: "#c084fc" },
  { key: "hired",       label: "Hired",       color: "#a5ce3a" },
  { key: "rejected",    label: "Passed",      color: "#8892a4" },
];

const CATEGORIES = {
  A: { color: "#a5ce3a", bg: "rgba(165,206,58,0.14)",  desc: "High chance — meets qualifications" },
  B: { color: "#fbbf24", bg: "rgba(251,191,36,0.14)",  desc: "Possible — partial qualifications / good location" },
  C: { color: "#ef4444", bg: "rgba(239,68,68,0.14)",   desc: "Low chance — minimal requirements or vague" },
};

// Apprenticeship buckets, in board order — most-ticketed first, with the two
// "nothing to place them on" buckets parked at the end. Keys match the
// hiring_candidates_apprentice_year_chk constraint and _resumeExtract.YEAR_VALUES.
const YEARS = [
  { key: "journeyman",     label: "Journeyman",     short: "JM",  color: "#a5ce3a" },
  { key: "4",              label: "4th Year",       short: "4th", color: "#7dd3fc" },
  { key: "3",              label: "3rd Year",       short: "3rd", color: "#60a5fa" },
  { key: "2",              label: "2nd Year",       short: "2nd", color: "#818cf8" },
  { key: "1",              label: "1st Year",       short: "1st", color: "#c084fc" },
  { key: "pre_apprentice", label: "Pre-apprentice", short: "Pre", color: "#fbbf24" },
  { key: "no_experience",  label: "No experience",  short: "New", color: "#f97316" },
  { key: "unknown",        label: "Unknown",        short: "?",   color: "#8892a4" },
];
const YEAR_BY_KEY = Object.fromEntries(YEARS.map(y => [y.key, y]));

const FONT = "'Roboto', sans-serif";
const HEADING_FONT = "'Bebas Neue', sans-serif";

function timeAgo(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

function CategoryBadge({ category, size = 22 }) {
  const cat = CATEGORIES[category];
  return (
    <span title={cat ? cat.desc : "Uncategorized"} style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: 6, flexShrink: 0,
      fontSize: size * 0.55, fontWeight: 800,
      color: cat ? cat.color : "#8892a4",
      background: cat ? cat.bg : "rgba(136,146,164,0.14)",
      border: `1px solid ${cat ? cat.color : "#8892a4"}40`,
    }}>
      {category || "?"}
    </span>
  );
}

function YearBadge({ year, title }) {
  const y = YEAR_BY_KEY[year];
  if (!y) return null;
  return (
    <span title={title || y.label} style={{
      fontSize: 10, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase",
      color: y.color, background: `${y.color}1f`, border: `1px solid ${y.color}44`,
      borderRadius: 5, padding: "1px 5px", flexShrink: 0, whiteSpace: "nowrap",
    }}>
      {y.short}
    </span>
  );
}

export default function Hiring() {
  const { session } = useSession() || {};
  const userEmail = session?.user?.email?.toLowerCase() || "";

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);     // candidate open in detail panel
  const [catFilter, setCatFilter] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [groupBy, setGroupBy] = useState("year");        // "year" | "stage"
  const [yearFilter, setYearFilter] = useState("all");
  const [searchDesc, setSearchDesc] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanReport, setScanReport] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    const { data, error: err } = await supabase
      .from("hiring_candidates")
      .select("*")
      .order("created_at", { ascending: false });
    if (err) {
      // Table not created yet (first deploy) — one-shot setup, then retry once.
      if (/hiring_candidates/.test(err.message || "")) {
        try {
          await apiFetch("/api/hiring?action=setup");
          const retry = await supabase.from("hiring_candidates").select("*").order("created_at", { ascending: false });
          if (!retry.error) { setCandidates(retry.data || []); setLoading(false); return; }
        } catch { /* fall through to the error banner */ }
      }
      setError(err.message);
    } else {
      setCandidates(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function patchCandidate(id, patch) {
    setCandidates(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));
    setSelected(prev => (prev && prev.id === id ? { ...prev, ...patch } : prev));
    const { error: err } = await supabase
      .from("hiring_candidates")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (err) { setError(`Save failed: ${err.message}`); load(); }
  }

  function moveStage(id, stage) {
    const c = candidates.find(x => x.id === id);
    if (!c || c.stage === stage) return;
    patchCandidate(id, { stage, stage_changed_at: new Date().toISOString() });
  }

  async function addNote(candidate) {
    const text = noteText.trim();
    if (!text) return;
    setSaving(true);
    const notes = [...(candidate.notes || []), { at: new Date().toISOString(), by: userEmail, text }];
    await patchCandidate(candidate.id, { notes });
    setNoteText("");
    setSaving(false);
  }

  async function deleteCandidate(candidate) {
    if (!window.confirm(`Delete ${candidate.candidate_name || "this candidate"}? This can't be undone.`)) return;
    const { error: err } = await supabase.from("hiring_candidates").delete().eq("id", candidate.id);
    if (err) { setError(`Delete failed: ${err.message}`); return; }
    setCandidates(prev => prev.filter(c => c.id !== candidate.id));
    setSelected(null);
  }

  async function openResume(file) {
    try {
      const r = await apiFetch(`/api/hiring?action=sign&path=${encodeURIComponent(file.path)}`);
      const data = await r.json();
      if (data?.url) window.open(data.url, "_blank", "noopener");
      else setError(`Couldn't open ${file.filename}: ${data?.error || "no URL returned"}`);
    } catch (e) {
      setError(`Couldn't open ${file.filename}: ${e.message}`);
    }
  }

  // Smart search: describe who you want in plain English (a sentence or a paragraph)
  // and the server ranks the pipeline against it, reading the extracted resume text
  // and the application email — not just the name field.
  async function runSearch(e) {
    if (e) e.preventDefault();
    const description = searchDesc.trim();
    if (!description) { setSearchResults(null); return; }
    setSearching(true);
    setError(null);
    try {
      const r = await apiFetch("/api/hiring?action=search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description, limit: 25 }),
      });
      const data = await r.json();
      if (!r.ok || data?.ok === false) throw new Error(data?.error || `search failed (${r.status})`);
      setSearchResults(data);
    } catch (err) {
      setError(`Search failed: ${err.message}`);
      setSearchResults(null);
    }
    setSearching(false);
  }

  // Dry run by default — shows what it WOULD file (and what it nearly filed) so the
  // detector can be judged before anything is written.
  async function runScan(commit) {
    setScanning(true);
    setError(null);
    try {
      // Extraction is capped per call so one invocation can't blow the function
      // timeout. Keep going until nothing is left; the sweep skips people it has
      // already filed, so repeating is safe.
      let merged = null;
      for (let pass = 0; pass < 8; pass++) {
        const r = await apiFetch(`/api/hiring?action=scan&days=90${commit ? "&commit=1" : ""}`);
        const data = await r.json();
        if (!r.ok || data?.ok === false) throw new Error(data?.error || `scan failed (${r.status})`);
        merged = merged
          ? { ...data, filed: (merged.filed || 0) + (data.filed || 0), candidates: data.candidates }
          : data;
        setScanReport(merged);
        if (!commit || !data.remaining) break;
        await load();
      }
      if (commit) load();
    } catch (err) {
      setError(`Inbox scan failed: ${err.message}`);
    }
    setScanning(false);
  }

  let visible = catFilter === "all" ? candidates : candidates.filter(c => c.category === catFilter);
  if (yearFilter !== "all") visible = visible.filter(c => (c.apprentice_year || "unknown") === yearFilter);

  const byStage = {};
  for (const s of STAGES) byStage[s.key] = [];
  for (const c of visible) (byStage[c.stage] || byStage.new).push(c);

  // Year board. Anything unclassified lands in "unknown" rather than vanishing.
  const byYear = {};
  for (const y of YEARS) byYear[y.key] = [];
  for (const c of visible) (byYear[c.apprentice_year] || byYear.unknown).push(c);

  const columns = groupBy === "year"
    ? YEARS.map(y => ({ ...y, items: byYear[y.key], dropStage: null }))
    : STAGES.map(s => ({ ...s, items: byStage[s.key], dropStage: s.key }));

  return (
    <div style={{ padding: "20px 16px 40px", maxWidth: 1500, margin: "0 auto", color: "#e8eaf0", fontFamily: FONT }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
        <div style={{ fontSize: 30, fontFamily: HEADING_FONT, letterSpacing: "0.03em", lineHeight: 1 }}>Hiring</div>
        <div style={{ fontSize: 13, color: "#8892a4", flex: 1, minWidth: 200 }}>
          {candidates.length} candidate{candidates.length === 1 ? "" : "s"} · forward resumes to ap@ with <b style={{ color: "#a5ce3a" }}>/hire A</b>, B or C
          <button onClick={() => setShowHelp(h => !h)} style={{
            marginLeft: 8, background: "none", border: "none", color: "#60a5fa",
            cursor: "pointer", fontSize: 12, fontFamily: FONT, textDecoration: "underline", padding: 0,
          }}>
            how it works
          </button>
        </div>
        <button onClick={() => setShowAdd(true)} style={{
          background: "#a5ce3a", color: "#0d0f12", border: "none", borderRadius: 8,
          padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT,
        }}>
          + Add candidate
        </button>
        <button onClick={() => { setLoading(true); load(); }} style={{
          background: "#1e2328", color: "#8892a4", border: "1px solid #2a3140", borderRadius: 8,
          padding: "8px 12px", fontSize: 13, cursor: "pointer", fontFamily: FONT,
        }}>
          ⟳ Refresh
        </button>
      </div>

      {showHelp && (
        <div style={{
          background: "#161a20", border: "1px solid #2a3140", borderRadius: 10,
          padding: "14px 16px", fontSize: 13, color: "#8892a4", lineHeight: 1.7, margin: "10px 0",
        }}>
          Forward (or send) any resume email to <b style={{ color: "#e8eaf0" }}>ap@browning-electric.ca</b> and put{" "}
          <b style={{ color: "#a5ce3a" }}>/hire</b> plus a category letter in the subject or at the top of the body:
          <div style={{ margin: "6px 0 6px 14px" }}>
            <div><b style={{ color: CATEGORIES.A.color }}>/hire A</b> — high chance, meets qualifications</div>
            <div><b style={{ color: CATEGORIES.B.color }}>/hire B</b> — might not meet everything, but a possible option (e.g. good location)</div>
            <div><b style={{ color: CATEGORIES.C.color }}>/hire C</b> — low chance, minimal requirements or vague</div>
          </div>
          Anything you write after the letter (or on the lines below) is saved as a note. The candidate's name, email,
          phone, the date they emailed, and any resume attachments are pulled in automatically — usually within a minute.
        </div>
      )}

      {/* ── Category filter ── */}
      <div style={{ display: "flex", gap: 8, margin: "12px 0 18px", flexWrap: "wrap" }}>
        {["all", "A", "B", "C"].map(k => {
          const active = catFilter === k;
          const color = k === "all" ? "#e8eaf0" : CATEGORIES[k].color;
          return (
            <button key={k} onClick={() => setCatFilter(k)} style={{
              background: active ? (k === "all" ? "rgba(232,234,240,0.12)" : CATEGORIES[k].bg) : "#161a20",
              color: active ? color : "#8892a4",
              border: `1px solid ${active ? color + "55" : "#2a3140"}`,
              borderRadius: 999, padding: "5px 14px", fontSize: 12, fontWeight: 700,
              cursor: "pointer", fontFamily: FONT,
            }}>
              {k === "all" ? "All" : `${k} · ${candidates.filter(c => c.category === k).length}`}
            </button>
          );
        })}
      </div>

      {/* ── Smart search ── */}
      <form onSubmit={runSearch} style={{
        background: "#12151a", border: "1px solid #2a3140", borderRadius: 12,
        padding: 14, marginBottom: 14,
      }}>
        <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 8 }}>
          Describe who you're looking for — a phrase or a whole paragraph. Every resume and
          application email gets read and ranked against it.
        </div>
        <textarea
          value={searchDesc}
          onChange={e => setSearchDesc(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runSearch(e); }}
          rows={3}
          placeholder="e.g. 3rd or 4th year apprentice living between Vernon and Salmon Arm who has done residential service work, can run their own van, and has a clean driver's abstract — bonus if they've touched generators or solar"
          style={{
            width: "100%", boxSizing: "border-box", background: "#1a1f26",
            border: "1px solid #2a3140", borderRadius: 8, color: "#e8eaf0",
            padding: "10px 12px", fontSize: 13, fontFamily: FONT, resize: "vertical",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
          <button type="submit" disabled={searching || !searchDesc.trim()} style={{
            background: searching || !searchDesc.trim() ? "#2a3140" : "#a5ce3a",
            color: searching || !searchDesc.trim() ? "#5b6372" : "#0d0f12",
            border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13,
            fontWeight: 700, cursor: searching || !searchDesc.trim() ? "default" : "pointer", fontFamily: FONT,
          }}>
            {searching ? "Reading resumes…" : "Find best fits"}
          </button>
          {searchResults && (
            <button type="button" onClick={() => { setSearchResults(null); setSearchDesc(""); }} style={{
              background: "#1e2328", color: "#8892a4", border: "1px solid #2a3140",
              borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer", fontFamily: FONT,
            }}>
              Clear results
            </button>
          )}
          <span style={{ fontSize: 11, color: "#5b6372" }}>⌘/Ctrl + Enter to search</span>
        </div>
      </form>

      {/* ── Search results ── */}
      {searchResults && (
        <div style={{
          background: "#12151a", border: "1px solid #a5ce3a44", borderRadius: 12,
          padding: 14, marginBottom: 18,
        }}>
          <div style={{ fontSize: 13, color: "#8892a4", marginBottom: 10 }}>
            {searchResults.matches?.length
              ? <>Ranked <b style={{ color: "#e8eaf0" }}>{searchResults.matches.length}</b> of {searchResults.pool} candidates</>
              : <>No good fits among {searchResults.pool} candidates. {searchResults.note || ""}</>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(searchResults.matches || []).map(m => {
              const tone = m.verdict === "strong" ? "#a5ce3a" : m.verdict === "possible" ? "#fbbf24" : "#8892a4";
              const full = candidates.find(c => c.id === m.id);
              return (
                <div key={m.id} style={{
                  background: "#1a1f26", border: `1px solid ${tone}33`, borderRadius: 10, padding: "12px 14px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 15, fontWeight: 800, color: tone, minWidth: 34,
                    }}>{m.score}</span>
                    <button onClick={() => { if (full) { setSelected(full); setNoteText(""); } }} style={{
                      background: "none", border: "none", padding: 0, cursor: full ? "pointer" : "default",
                      fontSize: 14, fontWeight: 600, color: "#e8eaf0", fontFamily: FONT,
                      textDecoration: full ? "underline" : "none",
                    }}>
                      {m.candidate_name || m.candidate_email || `Candidate ${m.id}`}
                    </button>
                    <YearBadge year={m.apprentice_year} />
                    <span style={{
                      fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em",
                      color: tone, background: `${tone}1f`, border: `1px solid ${tone}44`,
                      borderRadius: 5, padding: "1px 6px",
                    }}>{m.verdict}</span>
                    {m.location && <span style={{ fontSize: 11, color: "#5b6372" }}>📍 {m.location}</span>}
                    {m.candidate_phone && (
                      <a href={`tel:${m.candidate_phone}`} style={{ fontSize: 11, color: "#60a5fa", textDecoration: "none" }}>
                        {m.candidate_phone}
                      </a>
                    )}
                  </div>
                  {m.why && <div style={{ fontSize: 13, color: "#c9cfda", marginTop: 6, lineHeight: 1.55 }}>{m.why}</div>}
                  {(m.evidence || []).length > 0 && (
                    <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: "#8892a4", lineHeight: 1.6 }}>
                      {m.evidence.map((ev, i) => <li key={i}>{ev}</li>)}
                    </ul>
                  )}
                  {(m.gaps || []).length > 0 && (
                    <div style={{ fontSize: 12, color: "#fbbf24", marginTop: 6, lineHeight: 1.6 }}>
                      Gaps: {m.gaps.join(" · ")}
                    </div>
                  )}
                  {(m.resume_files || []).length > 0 && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                      {m.resume_files.map((f, i) => (
                        <button key={i} onClick={() => openResume(f)} style={{
                          background: "#1e2328", color: "#60a5fa", border: "1px solid #2a3140",
                          borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: FONT,
                        }}>
                          📄 {f.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Group-by + year filter + inbox scan ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 14px", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#8892a4" }}>Group by</span>
        {[{ k: "year", label: "Apprenticeship year" }, { k: "stage", label: "Stage" }].map(opt => {
          const active = groupBy === opt.k;
          return (
            <button key={opt.k} onClick={() => setGroupBy(opt.k)} style={{
              background: active ? "rgba(165,206,58,0.14)" : "#161a20",
              color: active ? "#a5ce3a" : "#8892a4",
              border: `1px solid ${active ? "#a5ce3a55" : "#2a3140"}`,
              borderRadius: 999, padding: "5px 14px", fontSize: 12, fontWeight: 700,
              cursor: "pointer", fontFamily: FONT,
            }}>
              {opt.label}
            </button>
          );
        })}
        <div style={{ flex: 1, minWidth: 8 }} />
        <button onClick={() => runScan(false)} disabled={scanning} title="Dry run — reads 90 days of mail and reports what it would file, without writing anything" style={{
          background: "#1e2328", color: scanning ? "#5b6372" : "#60a5fa", border: "1px solid #2a3140",
          borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: scanning ? "default" : "pointer", fontFamily: FONT,
        }}>
          {scanning ? "Scanning inbox…" : "🔎 Scan inbox (dry run)"}
        </button>
      </div>

      {yearFilter !== "all" && (
        <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 10 }}>
          Filtered to <b style={{ color: YEAR_BY_KEY[yearFilter]?.color }}>{YEAR_BY_KEY[yearFilter]?.label}</b>
          <button onClick={() => setYearFilter("all")} style={{
            marginLeft: 8, background: "none", border: "none", color: "#60a5fa",
            cursor: "pointer", fontSize: 12, fontFamily: FONT, textDecoration: "underline", padding: 0,
          }}>clear</button>
        </div>
      )}

      {/* ── Scan report ── */}
      {scanReport && (
        <div style={{
          background: "#12151a", border: "1px solid #2a3140", borderRadius: 12,
          padding: 14, marginBottom: 18, fontSize: 13, color: "#c9cfda",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <b style={{ color: "#e8eaf0" }}>
              {scanReport.dry_run ? "Dry run" : "Scan"} · {scanReport.scanned} emails read · {scanReport.threads ?? scanReport.detected} people
              {scanReport.folded ? ` (${scanReport.detected} messages, ${scanReport.folded} thread replies folded)` : ""}
              {!scanReport.dry_run && ` · ${scanReport.filed} filed`}
            </b>
            <div style={{ flex: 1 }} />
            {scanReport.dry_run && (scanReport.threads ?? scanReport.detected) > 0 && (
              <button onClick={() => runScan(true)} disabled={scanning} style={{
                background: "#a5ce3a", color: "#0d0f12", border: "none", borderRadius: 8,
                padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT,
              }}>
                File these {scanReport.threads ?? scanReport.detected} candidates
              </button>
            )}
            <button onClick={() => setScanReport(null)} style={{
              background: "none", border: "none", color: "#8892a4", fontSize: 16, cursor: "pointer", padding: 2,
            }}>✕</button>
          </div>
          {(scanReport.errors || []).length > 0 && (
            <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8, lineHeight: 1.6 }}>
              {scanReport.errors.slice(0, 4).map((e, i) => <div key={i}>⚠ {e.mailbox || ""} {e.error}</div>)}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(scanReport.candidates || []).map((c, i) => (
              <div key={i} style={{ borderTop: i ? "1px solid #1e2328" : "none", paddingTop: i ? 6 : 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <YearBadge year={c.apprentice_year} title={c.apprentice_year_basis} />
                  <b style={{ color: "#e8eaf0" }}>{c.candidate_name || c.from}</b>
                  <span style={{ fontSize: 11, color: "#5b6372" }}>
                    {c.subject} · score {c.score} ({c.confidence})
                    {c.action === "already_filed" ? " · already filed" : ""}
                  </span>
                </div>
                {c.summary && <div style={{ fontSize: 12, color: "#8892a4", marginTop: 2 }}>{c.summary}</div>}
                {(c.extracts || []).some(x => x.error) && (
                  <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 2 }}>
                    {c.extracts.filter(x => x.error).map(x => `${x.filename}: ${x.error}`).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
          {(scanReport.skipped || []).length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: "#8892a4" }}>
                {scanReport.skipped.length} near miss{scanReport.skipped.length === 1 ? "" : "es"} it decided against
              </summary>
              <div style={{ marginTop: 6, fontSize: 11, color: "#5b6372", lineHeight: 1.7 }}>
                {scanReport.skipped.slice(0, 25).map((s, i) => (
                  <div key={i}>
                    <b style={{ color: "#8892a4" }}>{s.subject || "(no subject)"}</b> — score {s.score}
                    {s.vetoes?.length ? ` · vetoed: ${s.vetoes.join("; ")}` : ""}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {error && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
          color: "#ef4444", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 14,
        }}>
          {error}
        </div>
      )}

      {/* ── Board ── */}
      {loading ? (
        <div style={{ color: "#8892a4", fontSize: 14, padding: 40, textAlign: "center" }}>Loading candidates…</div>
      ) : (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", overflowX: "auto", paddingBottom: 12 }}>
          {columns.map(stage => (
            <div
              key={stage.key}
              // Only the stage board is a drop target — apprenticeship year is a fact
              // about the candidate, not a status, so it must not be draggable.
              onDragOver={stage.dropStage ? (e => { e.preventDefault(); setDragOverStage(stage.key); }) : undefined}
              onDragLeave={stage.dropStage ? (() => setDragOverStage(s => (s === stage.key ? null : s))) : undefined}
              onDrop={stage.dropStage ? (e => {
                e.preventDefault();
                setDragOverStage(null);
                if (dragId != null) moveStage(dragId, stage.dropStage);
                setDragId(null);
              }) : undefined}
              style={{
                flex: "1 0 240px", minWidth: 240, maxWidth: 320,
                background: dragOverStage === stage.key ? "rgba(165,206,58,0.06)" : "#12151a",
                border: `1px solid ${dragOverStage === stage.key ? "#a5ce3a55" : "#2a3140"}`,
                borderRadius: 12, padding: 10, transition: "background 0.15s, border-color 0.15s",
              }}
            >
              <div
                // In year mode the header is a drill-down: "show me just the 2nd years,
                // laid out by where they are in the pipeline".
                onClick={groupBy === "year" && stage.items.length
                  ? (() => { setYearFilter(stage.key); setGroupBy("stage"); })
                  : undefined}
                title={groupBy === "year" && stage.items.length ? `Show only ${stage.label}, by stage` : undefined}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "4px 6px 10px",
                  cursor: groupBy === "year" && stage.items.length ? "pointer" : "default",
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: stage.color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#e8eaf0" }}>
                  {stage.label}
                </span>
                <span style={{ fontSize: 12, color: "#8892a4", marginLeft: "auto" }}>{stage.items.length}</span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 60 }}>
                {stage.items.length === 0 && (
                  <div style={{
                    border: "1px dashed #2a3140", borderRadius: 10, padding: "16px 10px",
                    fontSize: 12, color: "#5b6372", textAlign: "center",
                  }}>
                    {groupBy === "year"
                      ? "Nobody here yet"
                      : stage.key === "new" ? "Scan the inbox or forward a resume with /hire" : "Drag candidates here"}
                  </div>
                )}
                {stage.items.map(c => (
                  <div
                    key={c.id}
                    draggable={groupBy === "stage"}
                    onDragStart={() => setDragId(c.id)}
                    onDragEnd={() => { setDragId(null); setDragOverStage(null); }}
                    onClick={() => { setSelected(c); setNoteText(""); }}
                    style={{
                      background: "#1a1f26", border: "1px solid #2a3140", borderRadius: 10,
                      padding: "10px 12px", cursor: groupBy === "stage" ? "grab" : "pointer",
                      opacity: dragId === c.id ? 0.45 : 1,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <CategoryBadge category={c.category} />
                      <div style={{
                        fontSize: 14, fontWeight: 600, color: "#e8eaf0", flex: 1,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {c.candidate_name || c.candidate_email || "Unknown candidate"}
                      </div>
                    </div>
                    {c.subject && (
                      <div style={{
                        fontSize: 12, color: "#8892a4", marginBottom: 5,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {c.subject}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#5b6372", flexWrap: "wrap" }}>
                      <span>{timeAgo(c.emailed_at) || fmtDate(c.created_at)}</span>
                      {groupBy !== "year" && c.apprentice_year && (
                        <YearBadge year={c.apprentice_year} title={c.apprentice_year_basis} />
                      )}
                      {c.location && <span title="Location">📍 {c.location}</span>}
                      {(c.resume_files || []).length > 0 && <span title="Resume attached">📄 {(c.resume_files || []).length}</span>}
                      {c.resume_text && <span title="Resume text extracted — searchable">🔍</span>}
                      {(c.notes || []).length > 0 && <span title="Notes">💬 {(c.notes || []).length}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Detail panel ── */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#161a20", border: "1px solid #2a3140", borderRadius: 16,
              width: "100%", maxWidth: 620, maxHeight: "88vh", overflowY: "auto",
              padding: "22px 22px 26px", fontFamily: FONT,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 4 }}>
              <CategoryBadge category={selected.category} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#e8eaf0" }}>
                  {selected.candidate_name || selected.candidate_email || "Unknown candidate"}
                </div>
                {selected.subject && <div style={{ fontSize: 13, color: "#8892a4", marginTop: 2 }}>{selected.subject}</div>}
              </div>
              <button onClick={() => setSelected(null)} style={{
                background: "none", border: "none", color: "#8892a4", fontSize: 20, cursor: "pointer", padding: 4, lineHeight: 1,
              }}>✕</button>
            </div>

            {/* Stage buttons */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "14px 0" }}>
              {STAGES.map(s => {
                const active = selected.stage === s.key;
                return (
                  <button key={s.key} onClick={() => moveStage(selected.id, s.key)} style={{
                    background: active ? `${s.color}22` : "#1e2328",
                    color: active ? s.color : "#8892a4",
                    border: `1px solid ${active ? s.color + "66" : "#2a3140"}`,
                    borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700,
                    cursor: "pointer", fontFamily: FONT,
                  }}>
                    {s.label}
                  </button>
                );
              })}
            </div>

            {/* Category switch */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 12, color: "#8892a4", width: 70 }}>Category</span>
              {["A", "B", "C"].map(k => {
                const active = selected.category === k;
                return (
                  <button key={k} title={CATEGORIES[k].desc}
                    onClick={() => patchCandidate(selected.id, { category: active ? null : k })}
                    style={{
                      background: active ? CATEGORIES[k].bg : "#1e2328",
                      color: active ? CATEGORIES[k].color : "#8892a4",
                      border: `1px solid ${active ? CATEGORIES[k].color + "66" : "#2a3140"}`,
                      borderRadius: 8, width: 34, height: 30, fontSize: 13, fontWeight: 800,
                      cursor: "pointer", fontFamily: FONT,
                    }}>
                    {k}
                  </button>
                );
              })}
              {selected.category && (
                <span style={{ fontSize: 11, color: "#5b6372" }}>{CATEGORIES[selected.category].desc}</span>
              )}
            </div>

            {/* Facts */}
            <div style={{
              display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 8, columnGap: 10,
              fontSize: 13, background: "#12151a", border: "1px solid #2a3140",
              borderRadius: 10, padding: "12px 14px", marginBottom: 16,
            }}>
              <span style={{ color: "#8892a4" }}>Email</span>
              <span>{selected.candidate_email
                ? <a href={`mailto:${selected.candidate_email}`} style={{ color: "#60a5fa", textDecoration: "none" }}>{selected.candidate_email}</a>
                : "—"}</span>
              <span style={{ color: "#8892a4" }}>Phone</span>
              <span>{selected.candidate_phone
                ? <a href={`tel:${selected.candidate_phone}`} style={{ color: "#60a5fa", textDecoration: "none" }}>{selected.candidate_phone}</a>
                : "—"}</span>
              <span style={{ color: "#8892a4" }}>They emailed</span>
              <span>{fmtDate(selected.emailed_at)} {selected.emailed_at ? `(${timeAgo(selected.emailed_at)})` : ""}</span>
              <span style={{ color: "#8892a4" }}>Filed by</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{selected.forwarded_by || (selected.source === "manual" ? "added manually" : "—")}</span>
              <span style={{ color: "#8892a4" }}>In stage since</span>
              <span>{fmtDate(selected.stage_changed_at)}</span>
              {selected.location && <><span style={{ color: "#8892a4" }}>Location</span><span>{selected.location}</span></>}
              {selected.years_experience != null && (
                <><span style={{ color: "#8892a4" }}>Electrical exp.</span><span>{selected.years_experience} years</span></>
              )}
            </div>

            {/* Apprenticeship year — Claude's read, correctable by hand. The basis is
                shown so a wrong call is obvious rather than mysterious. */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 6, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                Apprenticeship year
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {YEARS.map(y => {
                  const active = (selected.apprentice_year || "unknown") === y.key;
                  return (
                    <button key={y.key}
                      onClick={() => patchCandidate(selected.id, {
                        apprentice_year: y.key,
                        apprentice_year_basis: active ? selected.apprentice_year_basis : `set by hand (${userEmail})`,
                      })}
                      style={{
                        background: active ? `${y.color}22` : "#1e2328",
                        color: active ? y.color : "#8892a4",
                        border: `1px solid ${active ? y.color + "66" : "#2a3140"}`,
                        borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 700,
                        cursor: "pointer", fontFamily: FONT,
                      }}>
                      {y.label}
                    </button>
                  );
                })}
              </div>
              {selected.apprentice_year_basis && (
                <div style={{ fontSize: 11, color: "#5b6372", marginTop: 6, lineHeight: 1.6 }}>
                  Basis: {selected.apprentice_year_basis}
                </div>
              )}
            </div>

            {/* Profile */}
            {selected.profile && Object.keys(selected.profile).length > 0 && (
              <div style={{
                background: "#12151a", border: "1px solid #2a3140", borderRadius: 10,
                padding: "12px 14px", marginBottom: 16, fontSize: 13, lineHeight: 1.6,
              }}>
                {selected.profile.summary && <div style={{ color: "#c9cfda" }}>{selected.profile.summary}</div>}
                {(selected.profile.skills || []).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "#8892a4" }}>
                    <b style={{ color: "#e8eaf0" }}>Skills:</b> {selected.profile.skills.join(" · ")}
                  </div>
                )}
                {(selected.profile.certs || []).length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "#8892a4" }}>
                    <b style={{ color: "#e8eaf0" }}>Certs:</b> {selected.profile.certs.join(" · ")}
                  </div>
                )}
                {(selected.profile.employers || []).length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "#8892a4" }}>
                    <b style={{ color: "#e8eaf0" }}>Employers:</b>{" "}
                    {selected.profile.employers.map(e => [e.title, e.name, e.dates].filter(Boolean).join(" @ ")).join(" · ")}
                  </div>
                )}
                {(selected.profile.concerns || []).length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#fbbf24" }}>
                    <b>Watch:</b> {selected.profile.concerns.join(" · ")}
                  </div>
                )}
              </div>
            )}

            {/* Resumes */}
            {(selected.resume_files || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 6, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>Resume files</div>
                {(selected.resume_files || []).map((f, i) => (
                  <button key={i} onClick={() => openResume(f)} style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    background: "#1e2328", border: "1px solid #2a3140", borderRadius: 8,
                    padding: "9px 12px", marginBottom: 6, cursor: "pointer",
                    color: "#a5ce3a", fontSize: 13, fontFamily: FONT, textAlign: "left",
                  }}>
                    📄 <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{f.filename}</span>
                    <span style={{ color: "#5b6372", fontSize: 11 }}>{f.size_kb ? `${f.size_kb} KB` : ""} · open</span>
                  </button>
                ))}
              </div>
            )}

            {/* Extracted resume text — what smart search actually reads. Shown so a
                bad extraction is visible instead of silently making someone unfindable. */}
            <div style={{ marginBottom: 16 }}>
              <details>
                <summary style={{ cursor: "pointer", fontSize: 12, color: "#8892a4", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  Resume text {selected.resume_text ? `(${selected.resume_text.length.toLocaleString()} chars)` : "— none extracted"}
                </summary>
                {(selected.resume_extract || []).length > 0 && (
                  <div style={{ fontSize: 11, color: "#5b6372", margin: "6px 0", lineHeight: 1.7 }}>
                    {selected.resume_extract.map((x, i) => (
                      <div key={i} style={{ color: x.error ? "#fbbf24" : "#5b6372" }}>
                        {x.filename} → {x.method}{x.chars ? ` · ${x.chars} chars` : ""}{x.error ? ` · ${x.error}` : ""}
                      </div>
                    ))}
                  </div>
                )}
                {selected.resume_text ? (
                  <pre style={{
                    background: "#12151a", border: "1px solid #2a3140", borderRadius: 10,
                    padding: "10px 12px", fontSize: 12, lineHeight: 1.6, color: "#c9cfda",
                    maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap", margin: "6px 0 0",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}>{selected.resume_text}</pre>
                ) : (
                  <div style={{ fontSize: 12, color: "#8892a4", marginTop: 6, lineHeight: 1.6 }}>
                    Nothing was read out of the attachments, so this person won't surface in a
                    smart search. Re-run extraction to try again:
                    <button onClick={async () => {
                      setSaving(true);
                      try {
                        const r = await apiFetch(`/api/hiring?action=reprocess&id=${selected.id}`);
                        const d = await r.json();
                        if (!r.ok || d?.ok === false) throw new Error(d?.error || `failed (${r.status})`);
                        await load();
                        setSelected(null);
                      } catch (e) { setError(`Reprocess failed: ${e.message}`); }
                      setSaving(false);
                    }} disabled={saving} style={{
                      marginLeft: 8, background: "#1e2328", color: "#60a5fa", border: "1px solid #2a3140",
                      borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: FONT,
                    }}>
                      {saving ? "Working…" : "Re-extract"}
                    </button>
                  </div>
                )}
              </details>
            </div>

            {/* Forward note */}
            {selected.forward_note && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 6, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>Your note from the forward</div>
                <div style={{
                  background: "rgba(165,206,58,0.07)", border: "1px solid rgba(165,206,58,0.2)",
                  borderRadius: 10, padding: "10px 14px", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6,
                }}>
                  {selected.forward_note}
                </div>
              </div>
            )}

            {/* Candidate's email body */}
            {selected.email_body && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 6, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>Their email</div>
                <div style={{
                  background: "#12151a", border: "1px solid #2a3140", borderRadius: 10,
                  padding: "10px 14px", fontSize: 13, color: "#c6ccd8",
                  whiteSpace: "pre-wrap", lineHeight: 1.6, maxHeight: 220, overflowY: "auto",
                }}>
                  {selected.email_body}
                </div>
              </div>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 6, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>Notes</div>
              {(selected.notes || []).length === 0 && (
                <div style={{ fontSize: 12, color: "#5b6372", marginBottom: 8 }}>No notes yet.</div>
              )}
              {(selected.notes || []).map((n, i) => (
                <div key={i} style={{
                  background: "#12151a", border: "1px solid #2a3140", borderRadius: 10,
                  padding: "8px 12px", marginBottom: 6, fontSize: 13,
                }}>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{n.text}</div>
                  <div style={{ fontSize: 11, color: "#5b6372", marginTop: 4 }}>
                    {(n.by || "").split("@")[0]} · {fmtDate(n.at)}
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addNote(selected); }}
                  placeholder="Add a note…"
                  style={{
                    flex: 1, background: "#1e2328", border: "1px solid #2a3140", borderRadius: 8,
                    padding: "9px 12px", color: "#e8eaf0", fontSize: 13, fontFamily: FONT, outline: "none",
                  }}
                />
                <button onClick={() => addNote(selected)} disabled={saving || !noteText.trim()} style={{
                  background: noteText.trim() ? "#a5ce3a" : "#1e2328",
                  color: noteText.trim() ? "#0d0f12" : "#5b6372",
                  border: "none", borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 700,
                  cursor: noteText.trim() ? "pointer" : "default", fontFamily: FONT,
                }}>
                  Add
                </button>
              </div>
            </div>

            <button onClick={() => deleteCandidate(selected)} style={{
              marginTop: 14, background: "none", border: "1px solid rgba(239,68,68,0.3)",
              color: "#ef4444", borderRadius: 8, padding: "7px 14px", fontSize: 12,
              cursor: "pointer", fontFamily: FONT,
            }}>
              Delete candidate
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <AddCandidateModal
          onClose={() => setShowAdd(false)}
          onAdded={(row) => { setCandidates(prev => [row, ...prev]); setShowAdd(false); }}
          userEmail={userEmail}
          onError={setError}
        />
      )}
    </div>
  );
}

// ── Manual add (walk-ins, phone applicants, referrals) ────────────────────────
function AddCandidateModal({ onClose, onAdded, userEmail, onError }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function save() {
    if (!form.name.trim()) return;
    setSaving(true);
    const now = new Date().toISOString();
    const row = {
      candidate_name: form.name.trim(),
      candidate_email: form.email.trim() || null,
      candidate_phone: form.phone.trim() || null,
      category: form.category || null,
      stage: "new",
      emailed_at: now,
      received_at: now,
      forwarded_by: userEmail || null,
      source: "manual",
      notes: form.note.trim() ? [{ at: now, by: userEmail, text: form.note.trim() }] : [],
    };
    const { data, error: err } = await supabase.from("hiring_candidates").insert(row).select().single();
    setSaving(false);
    if (err) { onError(`Add failed: ${err.message}`); return; }
    onAdded(data);
  }

  const inputStyle = {
    width: "100%", background: "#1e2328", border: "1px solid #2a3140", borderRadius: 8,
    padding: "10px 12px", color: "#e8eaf0", fontSize: 13, fontFamily: FONT,
    outline: "none", boxSizing: "border-box", marginBottom: 10,
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#161a20", border: "1px solid #2a3140", borderRadius: 16,
        width: "100%", maxWidth: 420, padding: "22px 22px 24px", fontFamily: FONT,
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#e8eaf0", marginBottom: 14 }}>Add candidate</div>
        <input style={inputStyle} placeholder="Name *" value={form.name} onChange={set("name")} autoFocus />
        <input style={inputStyle} placeholder="Email" value={form.email} onChange={set("email")} />
        <input style={inputStyle} placeholder="Phone" value={form.phone} onChange={set("phone")} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: "#8892a4" }}>Category</span>
          {["A", "B", "C"].map(k => (
            <button key={k} title={CATEGORIES[k].desc}
              onClick={() => setForm(f => ({ ...f, category: f.category === k ? "" : k }))}
              style={{
                background: form.category === k ? CATEGORIES[k].bg : "#1e2328",
                color: form.category === k ? CATEGORIES[k].color : "#8892a4",
                border: `1px solid ${form.category === k ? CATEGORIES[k].color + "66" : "#2a3140"}`,
                borderRadius: 8, width: 34, height: 30, fontSize: 13, fontWeight: 800,
                cursor: "pointer", fontFamily: FONT,
              }}>
              {k}
            </button>
          ))}
        </div>
        <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} placeholder="Note (optional)" value={form.note} onChange={set("note")} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            background: "#1e2328", color: "#8892a4", border: "1px solid #2a3140",
            borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer", fontFamily: FONT,
          }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving || !form.name.trim()} style={{
            background: form.name.trim() ? "#a5ce3a" : "#1e2328",
            color: form.name.trim() ? "#0d0f12" : "#5b6372",
            border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
            cursor: form.name.trim() ? "pointer" : "default", fontFamily: FONT,
          }}>
            {saving ? "Saving…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
